/**
 * Data layer for `postback-domain`. Thin typed fetch wrappers over /api/v1/postback-domain.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PostbackDomainRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPostbackDomainResponse {
	count: number;
	items: PostbackDomainRow[];
}

export interface CreatePostbackDomainBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/postback-domain";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPostbackDomainResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPostbackDomainResponse>(res);
}

export async function create(body: CreatePostbackDomainBody): Promise<PostbackDomainRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PostbackDomainRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const postbackDomainQueryKeys = {
	all: ["postback-domain"] as const,
	list: () => [...postbackDomainQueryKeys.all] as const,
	detail: (id: string) => [...postbackDomainQueryKeys.all, id] as const,
};
